import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CURRENCY, PRODUCTS, bySlug } from '../data/catalog';
import { useCart } from '../store/cart';
import { ProductCard } from '../components/ProductCard';
import { Button, Card, Money, Stars } from '../components/ui';
import { useTitle } from '../components/Layout';

export function Product() {
  const { slug = '' } = useParams();
  const product = bySlug(slug);
  const nav = useNavigate();
  const { add } = useCart();
  const [sku, setSku] = useState(product?.variants[0]?.sku ?? '');
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [tab, setTab] = useState<'opis' | 'dostawa' | 'opinie'>('opis');

  useTitle(product?.name ?? 'Produkt');

  useEffect(() => {
    if (!product) return;
    setSku(product.variants[0].sku);
    setQty(1);
    setTab('opis');
  }, [product]);

  // Musi być policzone PRZED efektem view_item niżej (i przed wczesnym
  // returnem), bo od tego zależy, kiedy efekt faktycznie odpali. `.find` na
  // stałej tablicy `product.variants` zwraca za każdym razem referencję do
  // tego samego obiektu, więc bezpiecznie ląduje w tablicy zależności efektu
  // bez potrzeby owijania w useMemo.
  const variant = product?.variants.find((v) => v.sku === sku);

  // view_item — rekomendowane zdarzenie GA4, wysyłane przy OTWARCIU karty
  // produktu ORAZ przy każdej zmianie wariantu — to inny item_id i inna
  // cena, więc w rozumieniu GA4 to inny produkt.
  //
  // Wyłącznie standardowe pola ze specyfikacji GA4 — żadnych własnych
  // parametrów. Pola bez pokrycia w danych (affiliation, coupon, location_id,
  // item_list_*) są świadomie pominięte, a nie wypełnione stałą wartością:
  // fabrykowanie "affiliation": "Nordform" dla każdego produktu nie niosłoby
  // żadnej informacji, tylko fałszywie sugerowało wypełnione pole.
  //
  // firedFor pilnuje dwóch rzeczy naraz: (1) żeby zdarzenie nie wystrzeliło
  // dwa razy dla tego samego wariantu — bez tego <StrictMode> (włączony w
  // main.tsx) dubluje ten efekt przy montowaniu (mount → cleanup → mount,
  // celowo, żeby wyłapać efekty niebezpieczne przy powtórnym uruchomieniu);
  // (2) żeby pominąć przejściowy render tuż po zmianie produktu, w którym
  // `sku` jest jeszcze wartością z POPRZEDNIEGO produktu (efekt wyżej
  // zdąży go poprawić dopiero w kolejnym cyklu) — wtedy `variant` wychodzi
  // `undefined` i po prostu czekamy na skorygowany render. SKU są unikalne
  // globalnie w całym katalogu, więc samo `variant.sku` wystarcza jako klucz.
  const firedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!product || !variant || firedFor.current === variant.sku) return;
    firedFor.current = variant.sku;
    const itemPrice = Math.round((product.price + variant.priceDelta) * 100) / 100;
    const discount =
      product.listPrice && product.listPrice > product.price
        ? Math.round((product.listPrice - product.price) * 100) / 100
        : undefined;

    window.dataLayer = window.dataLayer || [];
    // Czyścimy poprzedni stan modelu ecommerce w GTM, żeby zmienna Data
    // Layer Variable nie zlepiła tego zdarzenia z poprzednio wysłanym.
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'view_item',
      ecommerce: {
        currency: CURRENCY,
        value: itemPrice,
        items: [
          {
            item_id: variant.sku,
            item_name: product.name,
            item_brand: product.brand,
            item_category: product.category,
            ...(product.subcategory ? { item_category2: product.subcategory } : {}),
            ...(product.type ? { item_category3: product.type } : {}),
            item_variant: variant.label,
            price: itemPrice,
            quantity: 1,
            ...(discount !== undefined ? { discount } : {}),
          },
        ],
      },
    });
  }, [product, variant]);

  const related = useMemo(
    () => PRODUCTS.filter((p) => p.slug !== slug && p.category === product?.category).slice(0, 4),
    [slug, product],
  );

  if (!product) {
    return (
      <p className="py-16 text-center text-slate-400">
        Nie ma takiego produktu.{' '}
        <Link className="text-[#9ec5ff] underline" to="/katalog">
          Wróć do katalogu
        </Link>
      </p>
    );
  }

  const price = product.price + (variant?.priceDelta ?? 0);
  const unavailable = (variant?.stock ?? 0) === 0;

  return (
    <>
      <nav className="mb-4 text-[12px] text-slate-500">
        <Link to="/katalog" className="hover:text-slate-300">
          Katalog
        </Link>
        <span className="mx-1.5">›</span>
        <Link to={`/katalog?kategoria=${encodeURIComponent(product.category)}`} className="hover:text-slate-300">
          {product.category}
        </Link>
        {product.subcategory && (
          <>
            <span className="mx-1.5">›</span>
            <span>{product.subcategory}</span>
          </>
        )}
      </nav>

      <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
        <div>
          <Card className="p-5">
            <div
              className="mb-5 flex h-64 items-center justify-center rounded-xl text-7xl"
              style={{ background: `linear-gradient(135deg, ${product.color}22, ${product.color}08)` }}
            >
              {product.emoji}
            </div>

            <div className="mb-3 flex gap-1.5 border-b border-[#26304d]">
              {(
                [
                  ['opis', 'Opis'],
                  ['dostawa', 'Dostawa i zwroty'],
                  ['opinie', `Opinie (${product.reviews})`],
                ] as const
              ).map(([id, label]) => (
                // product-tab = zakres triggera Click. data-tab-id niesie
                // STABILNE id ('opis'/'dostawa'/'opinie'), nie wyświetlaną
                // etykietę — etykieta zakładki Opinie zawiera dynamiczną
                // liczbę recenzji ("Opinie (128)"), która różni się per
                // produkt i zaśmieciłaby wymiar dziesiątkami wariantów tej
                // samej, w gruncie rzeczy jednej wartości.
                <button
                  key={id}
                  onClick={() => setTab(id)}
                  className={`product-tab -mb-px border-b-2 px-3 py-2 text-[13px] transition ${
                    tab === id ? 'border-[#4f8cff] text-white' : 'border-transparent text-slate-400 hover:text-slate-200'
                  }`}
                  data-tab-id={id}
                >
                  {label}
                </button>
              ))}
            </div>

            {tab === 'opis' && <p className="text-[13.5px] leading-relaxed text-slate-300">{product.desc}</p>}
            {tab === 'dostawa' && (
              <ul className="space-y-1.5 text-[13px] text-slate-300">
                <li>• Realizacja: {product.fulfillment === 'magazyn' ? 'wysyłka w 24 h' : product.fulfillment === 'dropshipping' ? 'wysyłka w 3–5 dni' : 'produkcja na zamówienie, 10–14 dni'}</li>
                <li>• Darmowa dostawa od 2000 zł, poniżej od 15 zł.</li>
                <li>• Zwrot do 30 dni bez podawania przyczyny.</li>
                <li>• Gwarancja producenta: 5 lat na mechanizm.</li>
              </ul>
            )}
            {tab === 'opinie' && (
              <div className="space-y-3">
                <Stars value={product.rating} count={product.reviews} />
                {[
                  ['Marek K.', 5, 'Siedzę po osiem godzin dziennie, plecy dziękują. Montaż zajął kwadrans.'],
                  ['Anna W.', 4, 'Solidne wykonanie, ale kolor lekko odbiega od zdjęć.'],
                ].map(([who, stars, text]) => (
                  <div key={who as string} className="rounded-lg border border-[#26304d] p-3">
                    <p className="text-[12.5px] font-medium text-slate-200">
                      {who} <span className="ml-1 text-[#f5a623]">{'★'.repeat(stars as number)}</span>
                    </p>
                    <p className="mt-1 text-[12.5px] leading-relaxed text-slate-400">{text}</p>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        <div className="space-y-3">
          <Card className="p-5">
            <span className="text-[11px] uppercase tracking-wide text-slate-500">{product.brand}</span>
            <h1 className="mt-1 text-xl font-semibold text-white">{product.name}</h1>
            <div className="mt-1.5">
              <Stars value={product.rating} count={product.reviews} />
            </div>

            <div className="mt-4">
              <h2 className="mb-2 text-xs font-medium text-slate-300">Wariant</h2>
              <div className="flex flex-wrap gap-2">
                {product.variants.map((v) => (
                  <button
                    key={v.sku}
                    onClick={() => setSku(v.sku)}
                    disabled={v.stock === 0}
                    className={`rounded-lg border px-3 py-2 text-[12.5px] transition disabled:opacity-40 ${
                      v.sku === sku
                        ? 'border-[#4f8cff] bg-[#4f8cff]/10 text-white'
                        : 'border-[#26304d] text-slate-300 hover:border-slate-500'
                    }`}
                  >
                    {v.label}
                    {v.priceDelta > 0 && <span className="ml-1 text-[11px] text-slate-500">+{v.priceDelta} zł</span>}
                    {v.stock === 0 && <span className="ml-1 text-[11px] text-[#ffb0bb]">niedostępny</span>}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-5 flex items-baseline gap-2">
              <span className="text-2xl font-semibold text-white">
                <Money v={price} />
              </span>
              {product.listPrice && (
                <span className="text-[13px] text-slate-500 line-through">
                  <Money v={product.listPrice} />
                </span>
              )}
            </div>
            <p className="mt-1 text-[11.5px] text-slate-500">
              {unavailable
                ? 'Wariant chwilowo niedostępny'
                : (variant?.stock ?? 0) < 5
                  ? `Ostatnie sztuki: ${variant?.stock}`
                  : 'Dostępny'}
            </p>

            <div className="mt-4 flex items-end gap-2">
              <div>
                <label className="mb-1 block text-[11px] text-slate-500" htmlFor="ilosc">
                  Ilość
                </label>
                <input
                  id="ilosc"
                  type="number"
                  min={1}
                  max={9}
                  value={qty}
                  onChange={(e) => setQty(Math.max(1, Math.min(9, Number(e.target.value))))}
                  className="w-20 rounded-lg border border-[#26304d] bg-[#0b1020] px-3 py-2 text-sm text-white outline-none"
                />
              </div>
              <Button
                className="flex-1 py-2.5"
                disabled={unavailable}
                onClick={() => {
                  if (!variant) return;
                  add({ productId: product.id, sku: variant.sku, qty });
                  setAdded(true);
                  setTimeout(() => setAdded(false), 2000);

                  // add_to_cart — rekomendowane zdarzenie GA4. Ten sam zestaw
                  // standardowych pól co view_item, z dwiema różnicami: `quantity`
                  // to realna dodawana ilość (nie zawsze 1, użytkownik może
                  // zmienić liczbę w polu obok), a `value` to cena razy ta
                  // ilość — wartość tego, co faktycznie trafiło do koszyka, nie
                  // sama cena jednostkowa. Onclick, nie efekt — nie ma tu
                  // ryzyka podwójnego wywołania przez <StrictMode> (ten
                  // problem dotyczy tylko efektów, nie handlerów kliknięć),
                  // więc bez osobnego zabezpieczenia jak przy view_item.
                  const itemPrice = Math.round((product.price + variant.priceDelta) * 100) / 100;
                  const discount =
                    product.listPrice && product.listPrice > product.price
                      ? Math.round((product.listPrice - product.price) * 100) / 100
                      : undefined;

                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({ ecommerce: null });
                  window.dataLayer.push({
                    event: 'add_to_cart',
                    ecommerce: {
                      currency: CURRENCY,
                      value: Math.round(itemPrice * qty * 100) / 100,
                      items: [
                        {
                          item_id: variant.sku,
                          item_name: product.name,
                          item_brand: product.brand,
                          item_category: product.category,
                          ...(product.subcategory ? { item_category2: product.subcategory } : {}),
                          ...(product.type ? { item_category3: product.type } : {}),
                          item_variant: variant.label,
                          price: itemPrice,
                          quantity: qty,
                          ...(discount !== undefined ? { discount } : {}),
                        },
                      ],
                    },
                  });
                }}
              >
                {added ? '✓ Dodano do koszyka' : 'Dodaj do koszyka'}
              </Button>
            </div>

            {added && (
              <Button variant="soft" className="mt-2 w-full" onClick={() => nav('/koszyk')}>
                Przejdź do koszyka →
              </Button>
            )}
          </Card>

          <Card className="p-4 text-[12.5px] text-slate-400">
            <p>🚚 Darmowa dostawa od 2000 zł</p>
            <p className="mt-1.5">↩️ 30 dni na zwrot</p>
            <p className="mt-1.5">🛠️ 5 lat gwarancji</p>
          </Card>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-10">
          <h2 className="mb-3 text-lg font-semibold text-white">Podobne produkty</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {related.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}
    </>
  );
}
